import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_PREFIX = 'scrollPos:';

function readSavedScroll(key) {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
}

function writeSavedScroll(key, y) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, String(y));
  } catch {
    // best-effort scroll memory only
  }
}

export default function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const rafRef = useRef(null);

  useLayoutEffect(() => {
    const top = navigationType === 'POP' ? readSavedScroll(location.key) : 0;
    // `behavior: 'instant'` overrides the site-wide `scroll-behavior: smooth`
    // (src/index.css) — restoration/reset must be an instant jump, not an animation.
    const apply = () => window.scrollTo({ top, left: 0, behavior: 'instant' });
    apply();
    // A reflow just after mount (e.g. web font swap) can nudge the browser's
    // scroll position by a few px right after this runs — reassert once more
    // on the next frame so the jump/restore lands exactly on target.
    const raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [location.key, navigationType]);

  useEffect(() => {
    const key = location.key;
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        writeSavedScroll(key, window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [location.key]);

  return null;
}
